/**
 * One chapter = one StepCard: the question as the summary, the beats, the
 * illustration (or an honest placeholder), the honesty line, the footer.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { AlertCircle } from 'lucide-react';

import type { DropGenPlanDetail } from '@trafficpulse/shared';

import { StepCard } from '@/components/ui';
import { useAuth } from '@/providers/AuthProvider';

import type { BridgeParams } from './shell/TutorialBridge';
import { ChapterFooter } from './shell/ChapterFooter';
import type { TutorialChapterDef } from './tutorial-chapters';

/** The chapter's beats, then the gated ones this viewer may see. */
function useBeats(def: TutorialChapterDef): string[] {
  const { hasPermission } = useAuth();
  return [...def.beats, ...(def.gatedBeats ?? []).filter((g) => hasPermission(g.requires)).map((g) => g.beat)];
}

export const TutorialChapter: FC<{
  def: TutorialChapterDef;
  total: number;
  plan: DropGenPlanDetail;
  visited: boolean;
  onPrev: () => void;
  onNext: () => void;
  onBridge: (params: BridgeParams) => void;
}> = ({ def, total, plan, visited, onPrev, onNext, onBridge }) => {
  const { Illustration } = def;
  const beats = useBeats(def);
  return (
    <StepCard index={def.n} icon={def.Icon} title={def.title} summary={def.question} done={visited}>
      <div className="flex flex-col gap-4">
        <p className="text-sm font-medium">{def.question}</p>
        <ol className="flex flex-col gap-1.5 text-sm text-muted-foreground">
          {beats.map((beat, i) => (
            <li key={i} className="flex gap-2">
              <span className="w-5 shrink-0 font-mono text-xs text-muted-foreground/70">{i + 1}.</span>
              <span className="text-foreground">{beat}</span>
            </li>
          ))}
        </ol>
        {Illustration ? (
          <Illustration />
        ) : (
          <p className="rounded-lg border border-dashed border-border/60 px-3 py-6 text-center text-xs text-muted-foreground">
            The animated illustration for this chapter arrives with the next part of this phase.
          </p>
        )}
        {def.honesty ? (
          <p className="flex items-start gap-2 text-xs text-muted-foreground">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" />
            <span>
              <b className="text-foreground">Not built yet:</b> {def.honesty}
            </span>
          </p>
        ) : null}
        <ChapterFooter
          n={def.n}
          total={total}
          bridge={def.bridge}
          plan={plan}
          onPrev={onPrev}
          onNext={onNext}
          onBridge={onBridge}
        />
      </div>
    </StepCard>
  );
};
